import { useMemo } from 'react'
import type { Config } from 'wagmi'
import type { PoolTemplate } from '@curvefi/api/lib/pools'
import { useCurve } from '@evm-ui/features/connect-wallet'
import type { CurveApi } from '@evm-ui/features/connect-wallet/lib/types'
import { fetchTokenBalance, useTokenBalances } from '@evm-ui/hooks/useTokenBalance'
import type { ChainQuery, PoolQuery, UserQuery } from '@evm-ui/queries/query-types'
import type { Address } from '@primitives/address.utils'
import { maybe, notFalsyArray, recordValues } from '@primitives/objects.utils'
import { combineQueryState } from '@ui/features/queries/combine'
import type { FieldsOf } from '@ui/lib/validation/types'
import { tryGetPool } from '../pool.utils'

type Query = ChainQuery & UserQuery & PoolQuery
type Params = FieldsOf<Query>

const getAddresses = ({ underlyingCoinAddresses, wrappedCoinAddresses }: PoolTemplate) =>
  notFalsyArray(wrappedCoinAddresses, underlyingCoinAddresses) as Address[]

/** Fetch pool token balances and expose their combined loading/error state. */
export function usePoolTokenBalances({ chainId, userAddress, poolAddress }: Params) {
  const { curveApi, isHydrated } = useCurve()
  const tokenAddresses = useMemo(
    () => (isHydrated ? maybe(tryGetPool(poolAddress, curveApi), getAddresses) : undefined),
    [curveApi, isHydrated, poolAddress],
  )
  const balances = useTokenBalances({ chainId, userAddress, tokenAddresses })
  return useMemo(() => combineQueryState(...recordValues(balances)), [balances])
}

/** Temporary imperative function for some zustand slices to fetch all pool token balances */
export const fetchPoolTokenBalances = async (config: Config, curve: CurveApi, poolAddress: Address) => {
  const { wrappedCoinAddresses, underlyingCoinAddresses } = curve.getPool(poolAddress)
  const chainId = curve.chainId
  const userAddress = curve.signerAddress

  const balances = await Promise.allSettled([
    ...(wrappedCoinAddresses as Address[]).map(tokenAddress =>
      fetchTokenBalance(config, { chainId, userAddress, tokenAddress }).then(
        balance => [tokenAddress, balance] as const,
      ),
    ),
    ...(underlyingCoinAddresses as Address[]).map(tokenAddress =>
      fetchTokenBalance(config, { chainId, userAddress, tokenAddress }).then(
        balance => [tokenAddress, balance] as const,
      ),
    ),
  ])

  return Object.fromEntries(balances.filter(x => x.status === 'fulfilled').map(x => x.value))
}
